'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { trackLead } from '@/app/lib/analytics';

interface ServiceOption {
    slug: string;
    title: string;
}

interface ContactFormProps {
    labels: {
        name: string;
        email: string;
        message: string;
        submit: string;
    };
    locale: string;
    /** Services offered — rendered as selectable chips on the quote form. */
    services?: ServiceOption[];
    /** Pre-selected service slug (from a "Get a Quote" button, e.g. ?service=branding). */
    initialService?: string;
    /** 'quote' shows the extended brief fields (budget/timeline/services). */
    initialType?: 'general' | 'quote';
}

const t = (locale: string, en: string, ar: string) => (locale === 'en' ? en : ar);

export default function ContactForm({
    labels,
    locale,
    services = [],
    initialService,
    initialType = 'general',
}: ContactFormProps) {
    const isQuote = initialType === 'quote';

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        budget: '',
        timeline: '',
        message: '',
    });
    const [selectedServices, setSelectedServices] = useState<string[]>(
        initialService ? [initialService] : []
    );
    const [website, setWebsite] = useState(''); // Honeypot trap field
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Keep the pre-selected service in sync if the URL changes (client nav).
    useEffect(() => {
        if (initialService) {
            setSelectedServices((prev) => (prev.includes(initialService) ? prev : [...prev, initialService]));
        }
    }, [initialService]);

    // Conditional loading of reCAPTCHA v3 script
    useEffect(() => {
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
        if (!siteKey) return;

        const scriptId = 'google-recaptcha-v3-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const budgetOptions = [
        { value: '', label: t(locale, 'Select a range (optional)', 'اختر نطاقًا (اختياري)') },
        { value: 'under-5k', label: t(locale, 'Under 5,000 SAR', 'أقل من ٥٬٠٠٠ ريال') },
        { value: '5k-15k', label: t(locale, '5,000 – 15,000 SAR', '٥٬٠٠٠ – ١٥٬٠٠٠ ريال') },
        { value: '15k-50k', label: t(locale, '15,000 – 50,000 SAR', '١٥٬٠٠٠ – ٥٠٬٠٠٠ ريال') },
        { value: 'over-50k', label: t(locale, 'Over 50,000 SAR', 'أكثر من ٥٠٬٠٠٠ ريال') },
        { value: 'not-sure', label: t(locale, 'Not sure yet', 'غير متأكد بعد') },
    ];

    const timelineOptions = [
        { value: '', label: t(locale, 'Select a timeline (optional)', 'اختر الإطار الزمني (اختياري)') },
        { value: 'asap', label: t(locale, 'As soon as possible', 'في أسرع وقت ممكن') },
        { value: '1-month', label: t(locale, 'Within a month', 'خلال شهر') },
        { value: '1-3-months', label: t(locale, '1 – 3 months', '١ – ٣ أشهر') },
        { value: 'flexible', label: t(locale, 'Flexible / exploring', 'مرن / في مرحلة الاستكشاف') },
    ];

    const toggleService = (slug: string) => {
        setSelectedServices((prev) =>
            prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage(null);

        // 1. HONEYPOT TRAP FOR BOTS
        if (website) {
            setTimeout(() => {
                setStatus('success');
                setFormData({ name: '', email: '', phone: '', company: '', budget: '', timeline: '', message: '' });
                setSelectedServices([]);
                setWebsite('');
            }, 1000);
            return;
        }

        // 2. CLIENT-SIDE RATE LIMITING
        const lastSubmit = localStorage.getItem('last_submit_time');
        const now = Date.now();
        const LIMIT_DURATION = 60000; // 60 seconds

        if (lastSubmit && now - parseInt(lastSubmit) < LIMIT_DURATION) {
            const secondsLeft = Math.ceil((LIMIT_DURATION - (now - parseInt(lastSubmit))) / 1000);
            const limitMsg = locale === 'en'
                ? `You are submitting too fast. Please wait ${secondsLeft} seconds before trying again.`
                : `أنت تقوم بالإرسال بسرعة كبيرة. يرجى الانتظار ${secondsLeft} ثانية قبل الإرسال مرة أخرى.`;
            setErrorMessage(limitMsg);
            setStatus('error');
            return;
        }

        // 3. OPTIONAL RECAPTCHA V3 TOKEN GENERATION
        let recaptchaToken = '';
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
        if (siteKey && (window as any).grecaptcha) {
            try {
                recaptchaToken = await new Promise<string>((resolve, reject) => {
                    (window as any).grecaptcha.ready(() => {
                        (window as any).grecaptcha
                            .execute(siteKey, { action: 'contact_submit' })
                            .then((token: string) => resolve(token))
                            .catch((err: any) => reject(err));
                    });
                });
            } catch (err) {
                console.error('reCAPTCHA execution failed:', err);
            }
        }

        // Map selected service slugs to their human-readable titles for the CRM/email.
        const servicesInterested = selectedServices
            .map((slug) => services.find((s) => s.slug === slug)?.title || slug)
            .filter(Boolean);

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    submissionType: isQuote ? 'quote' : 'general',
                    servicesInterested,
                    budget: budgetOptions.find((b) => b.value === formData.budget && b.value)?.label || '',
                    timeline: timelineOptions.find((tl) => tl.value === formData.timeline && tl.value)?.label || '',
                    recaptchaToken,
                }),
            });

            if (res.ok) {
                // Server-confirmed submission only. The honeypot branch above also shows
                // the success state (so a bot is not told it was caught) — firing there
                // would count bots as leads.
                trackLead({
                    submissionType: isQuote ? 'quote' : 'general',
                    locale,
                    servicesInterested,
                    budget: budgetOptions.find((b) => b.value === formData.budget && b.value)?.label,
                    timeline: timelineOptions.find((tl) => tl.value === formData.timeline && tl.value)?.label,
                });

                setStatus('success');
                setFormData({ name: '', email: '', phone: '', company: '', budget: '', timeline: '', message: '' });
                setSelectedServices([]);
                localStorage.setItem('last_submit_time', Date.now().toString());
            } else {
                const responseData = await res.json().catch(() => ({}));
                const serverErr = responseData?.error || responseData?.errors?.[0]?.message || responseData?.message;
                const fallbackErr = locale === 'en'
                    ? 'Submission failed. Please check your inputs or try again.'
                    : 'فشل الإرسال. يرجى التحقق من المدخلات أو المحاولة مرة أخرى.';
                setErrorMessage(serverErr || fallbackErr);
                setStatus('error');
            }
        } catch (error) {
            const networkErr = locale === 'en'
                ? 'Network error. Please check your internet connection and try again.'
                : 'خطأ في الشبكة. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.';
            setErrorMessage(networkErr);
            setStatus('error');
        }
    };

    const inputClass =
        'w-full rounded-xl border border-[#081821]/15 bg-white p-4 text-[#081821] shadow-[0_8px_24px_rgba(8,24,33,0.08)] transition-all placeholder:text-[#081821]/40 focus:border-[#081821] focus:outline-none focus:ring-4 focus:ring-white/45';

    return (
        <div className="relative overflow-hidden w-full min-h-[480px] flex items-center justify-center">
            <AnimatePresence mode="wait">
                {status === 'success' ? (
                    <motion.div
                        key="success-card"
                        initial={{ opacity: 0, scale: 0.9, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -15 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className="w-full text-center flex flex-col items-center justify-center p-8 bg-gradient-to-br from-brand-text/[0.03] to-brand-text/[0.01] border border-brand-text/10 rounded-2xl shadow-2xl backdrop-blur-md"
                    >
                        <div className="relative flex items-center justify-center w-24 h-24 mb-6">
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0 }}
                                animate={{ scale: [1, 1.4, 1.6], opacity: [0.3, 0.15, 0] }}
                                transition={{ repeat: Infinity, duration: 2.2, ease: 'easeOut' }}
                                className="absolute inset-0 rounded-full bg-brand-cta/25"
                            />
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                className="absolute inset-0 rounded-full bg-gradient-to-tr from-brand-cta to-[#ea3228]/80 shadow-lg shadow-brand-cta/20 flex items-center justify-center"
                            />
                            <svg className="w-12 h-12 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <motion.path
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ delay: 0.3, duration: 0.6, ease: 'easeInOut' }}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>

                        <motion.h3
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            className="mb-3 font-heading text-2xl font-bold text-[#081821]"
                        >
                            {isQuote
                                ? t(locale, 'Quote Request Received!', 'تم استلام طلب عرض السعر!')
                                : t(locale, 'Message Sent Successfully!', 'تم إرسال رسالتك بنجاح!')}
                        </motion.h3>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            className="mb-8 max-w-md font-body text-base leading-relaxed text-[#081821]/70"
                        >
                            {t(
                                locale,
                                "Thank you for contacting nibras. We've received your details and our team will get in touch with you within 24 hours.",
                                'نشكرك على تواصلك مع نبراس. لقد تلقينا بياناتك وسيتواصل معك فريقنا في غضون ٢٤ ساعة.'
                            )}
                        </motion.p>

                        <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.5 }}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setStatus('idle')}
                            className="rounded-full border border-[#081821]/15 bg-white px-6 py-3 font-heading text-sm font-semibold text-[#081821] transition-all hover:bg-[#081821] hover:text-white"
                        >
                            {t(locale, 'Send another message', 'إرسال رسالة أخرى')}
                        </motion.button>
                    </motion.div>
                ) : (
                    <motion.form
                        key="contact-form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onSubmit={handleSubmit}
                        className="space-y-6 w-full"
                    >
                        {/* Stealth Honeypot Field */}
                        <div className="hidden" aria-hidden="true">
                                <label className="mb-2 block font-heading text-[#081821]">Website</label>
                            <input
                                type="text"
                                name="website"
                                value={website}
                                tabIndex={-1}
                                onChange={(e) => setWebsite(e.target.value)}
                                autoComplete="off"
                                    className="w-full rounded-xl border border-[#081821]/15 bg-white p-4 text-[#081821] focus:outline-none"
                            />
                        </div>

                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <label className="mb-2 block font-heading font-bold text-[#081821]">{labels.name}</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block font-heading font-bold text-[#081821]">{labels.email}</label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="grid gap-6 sm:grid-cols-2">
                            <div>
                                <label className="mb-2 block font-heading font-bold text-[#081821]">
                                    {t(locale, 'Phone', 'رقم الجوال')}
                                    <span className="text-sm font-normal text-[#081821]/75"> {t(locale, '(optional)', '(اختياري)')}</span>
                                </label>
                                <input
                                    type="tel"
                                    dir="ltr"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className="mb-2 block font-heading font-bold text-[#081821]">
                                    {t(locale, 'Company', 'الشركة')}
                                    <span className="text-sm font-normal text-[#081821]/75"> {t(locale, '(optional)', '(اختياري)')}</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        {/* Services of interest — shown when we have a services list to offer. */}
                        {services.length > 0 && (
                            <div>
                                <label className="mb-3 block font-heading font-bold text-[#081821]">
                                    {isQuote
                                        ? t(locale, 'What do you need help with?', 'الخدمات المطلوبة')
                                        : t(locale, 'Services of interest', 'الخدمات المطلوبة')}
                                    <span className="text-sm font-normal text-[#081821]/75"> {t(locale, '(optional)', '(اختياري)')}</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {services.map((service) => {
                                        const active = selectedServices.includes(service.slug);
                                        return (
                                            <button
                                                key={service.slug}
                                                type="button"
                                                onClick={() => toggleService(service.slug)}
                                                aria-pressed={active}
                                                className={`rounded-full border px-4 py-2 text-sm font-heading transition-all ${
                                                    active
                                                        ? 'border-[#081821] bg-[#081821] text-white shadow-md'
                                                        : 'border-white bg-white text-[#081821] shadow-sm hover:border-[#081821]/40 hover:-translate-y-0.5'
                                                }`}
                                            >
                                                {service.title}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Budget + timeline — only on the quote/brief flow. */}
                        {isQuote && (
                            <div className="grid gap-6 sm:grid-cols-2">
                                <div>
                                    <label className="mb-2 block font-heading font-bold text-[#081821]">
                                        {t(locale, 'Budget', 'الميزانية')}
                                    </label>
                                    <select
                                        value={formData.budget}
                                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                                        className={inputClass}
                                    >
                                        {budgetOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value} className="bg-white text-[#081821]">
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-2 block font-heading font-bold text-[#081821]">
                                        {t(locale, 'Timeline', 'الإطار الزمني')}
                                    </label>
                                    <select
                                        value={formData.timeline}
                                        onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                                        className={inputClass}
                                    >
                                        {timelineOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value} className="bg-white text-[#081821]">
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="mb-2 block font-heading font-bold text-[#081821]">
                                {isQuote
                                    ? t(locale, 'Tell us about your project', 'أخبرنا عن مشروعك')
                                    : labels.message}
                            </label>
                            <textarea
                                rows={4}
                                required
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                className={inputClass}
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={status === 'loading'}
                            className="w-full rounded-xl bg-[#081821] py-4 font-heading font-bold text-white shadow-[0_14px_32px_rgba(8,24,33,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-[#081821] disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-[#081821] disabled:hover:text-white"
                        >
                            {status === 'loading'
                                ? t(locale, 'Sending...', 'جاري الإرسال...')
                                : isQuote
                                  ? t(locale, 'Request My Quote', 'اطلب عرض السعر')
                                  : labels.submit}
                        </button>

                        {status === 'error' && (
                            <p className="text-red-500 text-sm mt-2 text-center font-body leading-relaxed animate-pulse">
                                {errorMessage || t(locale, 'Something went wrong. Please try again.', 'حدث خطأ. يرجى المحاولة مرة أخرى.')}
                            </p>
                        )}
                    </motion.form>
                )}
            </AnimatePresence>
        </div>
    );
}
